import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Badge, Form } from 'react-bootstrap';
import { FaArrowLeft, FaEdit, FaServer, FaPlus, FaCog } from 'react-icons/fa';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import { cacheService, CACHE_KEYS, CACHE_TTL } from '../utils/cacheService';
import './AgentDetails.css';

const AgentDetails = () => {
  const { parentId } = useParams();
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState(null);
  const [miniAgents, setMiniAgents] = useState([]);
  const [microAgents, setMicroAgents] = useState([]);
  const [microServices, setMicroServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('mini');
  const [cacheStatus, setCacheStatus] = useState({ 
    agent: { fromCache: false, timestamp: null },
    microAgents: { fromCache: false, timestamp: null }
  });
  const [updatingStatus, setUpdatingStatus] = useState({}); // Track which agents/services are being updated

  useEffect(() => {
    fetchAgentDetails(parentId);
  }, [parentId]);

  // Handle status updates for micro-agents and mini-agents
  const handleStatusUpdate = async (agentId, newStatus, agentType, currentAgent) => {
    const updateKey = `${agentType}_${agentId}`;
    
    try {
      setUpdatingStatus(prev => ({ ...prev, [updateKey]: true }));
      
      console.log(`Updating ${agentType} status:`, { agentId, newStatus });
      
      // Make API call to update status
      const response = await api.makeAuthenticatedRequest('POST', '/db/update-micro-agent-status', {
        agent_id: "1", // Use agent_id 1 for AgentDeveloper
        micro_agent_id: agentId,
        status: newStatus
      });
      
      console.log('Status update response:', response);
      
      if (response && response.success) {
        // Update the local state to reflect the change
        if (agentType === 'mini') {
          setMiniAgents(prev => 
            prev.map(agent => 
              agent.id === agentId 
                ? { ...agent, status: newStatus }
                : agent
            )
          );
        } else {
          setMicroAgents(prev => 
            prev.map(agent => 
              agent.id === agentId 
                ? { ...agent, status: newStatus }
                : agent
            )
          );
        }
        
        console.log(`Successfully updated ${agentType} agent status to: ${newStatus}`);
      } else {
        throw new Error(response?.error || 'Failed to update status');
      }
    } catch (err) {
      console.error(`Error updating ${agentType} agent status:`, err);
      setError(`Failed to update ${agentType} agent status: ${err.message}`);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  // Handle status updates for micro-services
  const handleServiceStatusUpdate = async (serviceId, newStatus) => {
    const updateKey = `service_${serviceId}`;
    
    try {
      setUpdatingStatus(prev => ({ ...prev, [updateKey]: true }));
      
      console.log(`Updating micro-service status:`, { serviceId, newStatus });
      
      // Make API call to update micro-service status
      const response = await api.makeAuthenticatedRequest('POST', '/db/update-micro-service-status', {
        agent_id: "1", // Use agent_id 1 for AgentDeveloper
        micro_service_id: serviceId,
        status: newStatus
      });
      
      console.log('Service status update response:', response);
      
      if (response && response.success) {
        // Update the local state to reflect the change
        setMicroServices(prev => 
          prev.map(service => 
            service.id === serviceId 
              ? { ...service, status: newStatus }
              : service
          )
        );
        
        console.log(`Successfully updated micro-service status to: ${newStatus}`);
      } else {
        throw new Error(response?.error || 'Failed to update micro-service status');
      }
    } catch (err) {
      console.error(`Error updating micro-service status:`, err);
      setError(`Failed to update micro-service status: ${err.message}`);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  const fetchAgentDetails = async (parentIdOrName, forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const cacheKey = `${CACHE_KEYS.AGENTS_LIST}_${parentIdOrName}`;
      
      // Check cache first unless forcing refresh
      if (!forceRefresh) {
        const cachedData = cacheService.get(cacheKey);
        
        if (cachedData) {
          console.log('Loading agent details from cache');
          setAgent(cachedData.agent);
          setMicroAgents(cachedData.microAgents || []);
          setMiniAgents(cachedData.miniAgents || []);
          setMicroServices(cachedData.microServices || []);
          setCacheStatus({
            agent: { fromCache: true, timestamp: cachedData.timestamp },
            microAgents: { fromCache: true, timestamp: cachedData.timestamp }
          });
          setLoading(false);
          
          // Fetch fresh data in background
          fetchFreshData(parentIdOrName, cacheKey);
          return;
        }
      } else {
        // Clear cache if forcing refresh
        cacheService.remove(cacheKey);
      }
      
      // Fetch fresh data
      await fetchFreshData(parentIdOrName, cacheKey);
      
    } catch (error) {
      console.error('Error fetching agent data:', error);
      setError('Error fetching agent data: ' + error);
      setLoading(false);
    }
  };

  const fetchFreshData = async (parentIdOrName, cacheKey) => {
    try {
      // Fetch agent details using new API
      console.log(`Fetching agent details for: ${parentIdOrName}`);
      const agentsResponse = await api.getAgents();
      
      // Try to find the agent by name or ID
      let agent = null;
      
      if (agentsResponse && agentsResponse.agents) {
        agent = agentsResponse.agents.find(a => 
          a.uniqueIdentifier === parentIdOrName || 
          a.agentName === parentIdOrName || 
          a.repoName === parentIdOrName
        );
      }
      
      if (agent) {
        const formattedAgent = {
          id: agent.uniqueIdentifier,
          name: agent.agentName || agent.repoName,
          description: agent.agentDescription || `${agent.repoName} agent`,
          status: agent.commissioned ? 'active' : 'inactive',
          deployedInDev: agent.deployedInDev,
          deployedInProd: agent.deployedInProd,
          repoName: agent.repoName,
          repoAccount: agent.repoAccount,
          config: agent.config || {}
        };
        setAgent(formattedAgent);
        
        const parentAgentName = agent.agentName || agent.repoName || parentIdOrName;
        
        // Fetch micro agents
        let mappedMicroAgents = [];
        try {
          console.log(`Fetching micro agents for parent: ${parentAgentName}`);
          const microAgentsResponse = await api.getMicroAgents(parentAgentName);
          
          if (microAgentsResponse && microAgentsResponse.success && microAgentsResponse.microAgents) {
            mappedMicroAgents = microAgentsResponse.microAgents
              .filter(agent => !agent.isMiniAgent)
              .map(agent => ({
                id: agent.uniqueIdentifier,
                agentNumber: agent.agentNumber,
                name: agent.microAgentName,
                description: agent.microAgentDescription,
                parentAgentName: agent.parentAgentName,
                status: agent.status || (agent.commissioned ? 'commissioned' : 'not commissioned'),
                devStatus: agent.deployedInDev ? 'deployed' : 'not deployed',
                prodStatus: agent.deployedInProd ? 'deployed' : 'not deployed',
                isMiniAgent: agent.isMiniAgent
              }));
            setMicroAgents(mappedMicroAgents);
          } else {
            setMicroAgents([]);
          }
        } catch (err) {
          console.error('Error fetching micro agents:', err);
          setMicroAgents([]);
        }

        // Fetch mini agents
        let mappedMiniAgents = [];
        try {
          console.log(`Fetching mini agents for parent: ${parentAgentName}`);
          const miniAgentsResponse = await api.getMiniAgents(parentAgentName);
          
          if (miniAgentsResponse && miniAgentsResponse.success && miniAgentsResponse.microAgents) {
            mappedMiniAgents = miniAgentsResponse.microAgents
              .filter(agent => agent.isMiniAgent)
              .map(agent => ({
                id: agent.uniqueIdentifier,
                agentNumber: agent.agentNumber,
                name: agent.microAgentName,
                description: agent.microAgentDescription,
                parentAgentName: agent.parentAgentName,
                status: agent.status || (agent.commissioned ? 'commissioned' : 'not commissioned'),
                devStatus: agent.deployedInDev ? 'deployed' : 'not deployed',
                prodStatus: agent.deployedInProd ? 'deployed' : 'not deployed',
                isMiniAgent: agent.isMiniAgent
              }));
            setMiniAgents(mappedMiniAgents);
          } else {
            setMiniAgents([]);
          }
        } catch (err) {
          console.error('Error fetching mini agents:', err);
          setMiniAgents([]);
        }

        // Fetch micro services
        let mappedMicroServices = [];
        try {
          console.log(`Fetching micro services for parent: ${parentAgentName}`);
          const microServicesResponse = await api.getMicroServices(agent.uniqueIdentifier || "1");
          
          if (microServicesResponse && microServicesResponse.success && microServicesResponse.microServices) {
            mappedMicroServices = microServicesResponse.microServices.map(service => ({
              id: service.micro_service_id,
              name: service.name,
              description: service.description,
              devStatus: service.deployed_development ? 'deployed' : 'not deployed',
              prodStatus: service.deployed_production ? 'deployed' : 'not deployed',
              endpoint: service.endpoint,
              method: service.method,
              status: service.status || 'development', // Default to development if no status
              active: service.active,
              agent_id: service.agent_id,
              agent_name: service.agent_name
            }));
            setMicroServices(mappedMicroServices);
          } else {
            setMicroServices([]);
          }
        } catch (err) {
          console.error('Error fetching micro services:', err);
          setMicroServices([]);
        }

        // Cache the complete data
        const cacheData = {
          agent: formattedAgent,
          microAgents: mappedMicroAgents,
          miniAgents: mappedMiniAgents,
          microServices: mappedMicroServices,
          timestamp: Date.now()
        };
        cacheService.set(cacheKey, cacheData, CACHE_TTL.MEDIUM);
        setCacheStatus({
          agent: { fromCache: false, timestamp: Date.now() },
          microAgents: { fromCache: false, timestamp: Date.now() }
        });
      } else {
        throw new Error('Agent not found');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching fresh agent data:', err);
      if (!cacheStatus.agent.fromCache) {
        setError('Error fetching agent data: ' + err);
      }
      setLoading(false);
    }
  };

  const handleNavigateToConfig = () => {
    navigate(`/agents/${parentId}/configuration`);
  };

  const handleNavigateToMiniAgent = (miniAgentId) => {
    navigate(`/agents/${parentId}/mini-agents/${miniAgentId}`);
  };

  const handleNavigateToMicroAgent = (microAgentId) => {
    navigate(`/agents/${parentId}/micro-agents/${microAgentId}`);
  };

  const handleNavigateToMicroService = (microServiceId) => {
    navigate(`/agents/${parentId}/micro-services/${microServiceId}`);
  };

  const handleNavigateToTesting = (agentType, id) => {
    navigate(`/agents/${parentId}/${agentType}/${id}/testing`);
  };

  const handleNavigateToServiceTesting = (microServiceId) => {
    navigate(`/agents/${parentId}/micro-services/${microServiceId}/testing`);
  };

  const handleNavigateToLogs = (agentType, id) => {
    navigate(`/agents/${parentId}/${agentType}/${id}/logs`);
  };

  const handleNavigateToDeployment = () => {
    navigate(`/agents/deploy/${parentId}`);
  };

  const handleBackToDevelop = () => {
    navigate('/agents/develop');
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  // Helper function to get status badge color based on status string
  const getStatusBadgeColor = (status) => {
    if (!status) return 'secondary';
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes('commissioned') || statusLower.includes('production') || statusLower.includes('active')) {
      return 'success';
    } else if (statusLower.includes('development') || statusLower.includes('dev')) {
      return 'primary';
    } else if (statusLower.includes('failed') || statusLower.includes('error')) {
      return 'danger';
    } else if (statusLower.includes('pending') || statusLower.includes('waiting')) {
      return 'warning';
    }
    return 'secondary';
  };

  // Render status dropdown component
  const renderStatusDropdown = (agent, agentType) => {
    const updateKey = `${agentType}_${agent.id}`;
    const isUpdating = updatingStatus[updateKey];
    
    // Normalize the current status to match dropdown options
    const normalizeStatus = (status) => {
      if (!status) return 'development';
      const statusLower = status.toLowerCase();
      if (statusLower.includes('prod') || statusLower === 'production') return 'production';
      if (statusLower.includes('test') || statusLower === 'testing') return 'testing';
      if (statusLower.includes('deprecat') || statusLower === 'deprecated') return 'deprecated';
      return 'development';
    };

    const currentStatus = normalizeStatus(agent.status);
    
    return (
      <Form.Select
        value={currentStatus}
        onChange={(e) => handleStatusUpdate(agent.id, e.target.value, agentType, agent)}
        disabled={isUpdating}
        size="sm"
        style={{ minWidth: '120px' }}
      >
        <option value="development">Development</option>
        <option value="testing">Testing</option>
        <option value="production">Production</option>
        <option value="deprecated">Deprecated</option>
      </Form.Select>
    );
  };

  // Render status dropdown component for micro-services
  const renderServiceStatusDropdown = (service) => {
    const updateKey = `service_${service.id}`;
    const isUpdating = updatingStatus[updateKey];
    
    // Normalize the current status to match dropdown options
    const normalizeStatus = (status) => {
      if (!status) return 'development';
      const statusLower = status.toLowerCase();
      if (statusLower.includes('prod') || statusLower === 'production') return 'production';
      if (statusLower.includes('test') || statusLower === 'testing') return 'testing';
      if (statusLower.includes('deprecat') || statusLower === 'deprecated') return 'deprecated';
      return 'development';
    };

    const currentStatus = normalizeStatus(service.status);
    
    return (
      <Form.Select
        value={currentStatus}
        onChange={(e) => handleServiceStatusUpdate(service.id, e.target.value)}
        disabled={isUpdating}
        size="sm"
        style={{ minWidth: '120px' }}
      >
        <option value="development">Development</option>
        <option value="testing">Testing</option>
        <option value="production">Production</option>
        <option value="deprecated">Deprecated</option>
      </Form.Select>
    );
  };

  if (loading) {
    return (
      <Container className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
        <div className="text-center">
          <Spinner />
          <p className="mt-3">Loading agent data...</p>
        </div>
      </Container>
    );
  }

  if (error || !agent) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <h4>Error Loading Agent</h4>
          <p>{error || 'Agent not found'}</p>
          <div className="mt-3">
            <Button variant="outline-primary" onClick={handleBackToDevelop}>
              Back to Agents List
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4 agent-details-container">
      <div className="agent-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="display-4">{agent.name}</h1>
          <p className="text-muted lead">{agent.description}</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleBackToDevelop}>
            <FaArrowLeft className="me-2" />
            Back to Agent List
          </Button>
          <Button variant="primary" onClick={handleNavigateToConfig}>
            <FaCog className="me-2" />
            Configuration
          </Button>
        </div>
      </div>

      {/* Enhanced navigation tabs with centered labels */}
      <div className="mb-4">
        <div className="nav-tabs nav-justified tesla-tabs">
          <div 
            className={`nav-item nav-link ${activeSection === 'mini' ? 'active' : ''}`}
            onClick={() => handleSectionChange('mini')}
            style={{cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center'}}
          >
            Mini-Agents
          </div>
          <div 
            className={`nav-item nav-link ${activeSection === 'micro' ? 'active' : ''}`}
            onClick={() => handleSectionChange('micro')}
            style={{cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center'}}
          >
            Micro-Agents
          </div>
          <div 
            className={`nav-item nav-link ${activeSection === 'microservices' ? 'active' : ''}`}
            onClick={() => handleSectionChange('microservices')}
            style={{cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center'}}
          >
            Micro-Services
          </div>
        </div>
      </div>

      {/* Mini-Agents Section */}
      {activeSection === 'mini' && (
        <Card className="p-0 mb-4">
          <Card.Header className="d-flex justify-content-center align-items-center">
            <h4 className="mb-0">Mini-Agents</h4>
          </Card.Header>
          <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {miniAgents.length === 0 ? (
              <Alert variant="info">
                No mini-agents found for this parent agent.
              </Alert>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{width: '8%'}}>ID</th>
                      <th style={{width: '30%'}}>Name</th>
                      <th style={{width: '15%'}}>Status</th>
                      <th style={{width: '11%'}}>Dev Status</th>
                      <th style={{width: '11%'}}>Prod Status</th>
                      <th style={{width: '25%'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {miniAgents
                      .filter(agent => agent.parentAgentName === agent.name || agent.parentAgentName === parentId)
                      .sort((a, b) => Number(a.agentNumber) - Number(b.agentNumber))
                      .map(agent => {
                        const updateKey = `mini_${agent.id}`;
                        const isUpdating = updatingStatus[updateKey];
                        
                        return (
                          <tr key={agent.id}>
                            <td>
                              <span className="agent-number">{agent.agentNumber}</span>
                            </td>
                            <td>{agent.name}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                {isUpdating && (
                                  <Spinner size="sm" className="me-2" />
                                )}
                                {renderStatusDropdown(agent, 'mini')}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${agent.devStatus === 'deployed' ? 'bg-success' : 'bg-secondary'}`}>
                                {agent.devStatus || 'Not Deployed'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${agent.prodStatus === 'deployed' ? 'bg-success' : 'bg-secondary'}`}>
                                {agent.prodStatus || 'Not Deployed'}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2 justify-content-start">
                                <Button 
                                  size="sm" 
                                  variant="primary" 
                                  onClick={() => handleNavigateToMiniAgent(agent.id)}
                                >
                                  Develop
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="info" 
                                  onClick={() => handleNavigateToTesting('mini-agents', agent.id)}
                                >
                                  Test
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="secondary" 
                                  onClick={() => handleNavigateToLogs('mini-agents', agent.id)}
                                >
                                  Logs
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Micro-Agents Section */}
      {activeSection === 'micro' && (
        <Card className="p-0 mb-4">
          <Card.Header className="d-flex justify-content-center align-items-center">
            <h4 className="mb-0">Micro-Agents</h4>
          </Card.Header>
          <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {microAgents.length === 0 ? (
              <Alert variant="info">
                No micro-agents found for this parent agent.
              </Alert>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{width: '8%'}}>ID</th>
                      <th style={{width: '30%'}}>Name</th>
                      <th style={{width: '15%'}}>Status</th>
                      <th style={{width: '11%'}}>Dev Status</th>
                      <th style={{width: '11%'}}>Prod Status</th>
                      <th style={{width: '25%'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {microAgents
                      .filter(agent => agent.parentAgentName === agent.name || agent.parentAgentName === parentId)
                      .sort((a, b) => Number(a.agentNumber) - Number(b.agentNumber))
                      .map(agent => {
                        const updateKey = `micro_${agent.id}`;
                        const isUpdating = updatingStatus[updateKey];
                        
                        return (
                          <tr key={agent.id}>
                            <td>
                              <span className="agent-number">{agent.agentNumber}</span>
                            </td>
                            <td>{agent.name}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                {isUpdating && (
                                  <Spinner size="sm" className="me-2" />
                                )}
                                {renderStatusDropdown(agent, 'micro')}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${agent.devStatus === 'deployed' ? 'bg-success' : 'bg-secondary'}`}>
                                {agent.devStatus || 'Not Deployed'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${agent.prodStatus === 'deployed' ? 'bg-success' : 'bg-secondary'}`}>
                                {agent.prodStatus || 'Not Deployed'}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2 justify-content-start">
                                <Button 
                                  size="sm" 
                                  variant="primary" 
                                  onClick={() => handleNavigateToMicroAgent(agent.id)}
                                >
                                  Develop
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="info" 
                                  onClick={() => handleNavigateToTesting('micro-agents', agent.id)}
                                >
                                  Test
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="secondary" 
                                  onClick={() => handleNavigateToLogs('micro-agents', agent.id)}
                                >
                                  Logs
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}  
          </Card.Body>
        </Card>
      )}

      {/* Micro-Services Section */}
      {activeSection === 'microservices' && (
        <Card className="p-0 mb-4">
          <Card.Header className="d-flex justify-content-center align-items-center">
            <h4 className="mb-0">Micro-Services</h4>
          </Card.Header>
          <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {microServices.length === 0 ? (
              <Alert variant="info">
                No micro-services found for this parent agent.
              </Alert>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{width: '20%'}}>Name</th>
                      <th style={{width: '28%'}}>Description</th>
                      <th style={{width: '13%'}}>Status</th>
                      <th style={{width: '11%'}}>Dev Status</th>
                      <th style={{width: '11%'}}>Prod Status</th>
                      <th style={{width: '17%'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {microServices
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(service => {
                        const updateKey = `service_${service.id}`;
                        const isUpdating = updatingStatus[updateKey];
                        
                        return (
                          <tr key={service.id}>
                            <td>{service.name}</td>
                            <td>
                              <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {service.description || 'No description available'}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {isUpdating && (
                                  <Spinner size="sm" className="me-2" />
                                )}
                                {renderServiceStatusDropdown(service)}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${service.devStatus === 'deployed' ? 'bg-success' : 'bg-secondary'}`}>
                                {service.devStatus || 'Not Deployed'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${service.prodStatus === 'deployed' ? 'bg-success' : 'bg-secondary'}`}>
                                {service.prodStatus || 'Not Deployed'}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2 justify-content-start">
                                <Button 
                                  size="sm" 
                                  variant="primary" 
                                  onClick={() => handleNavigateToMicroService(service.id)}
                                >
                                  Develop
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="info" 
                                  onClick={() => handleNavigateToServiceTesting(service.id)}
                                >
                                  Test
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                    })}
                  </tbody>
                </table>
              </div>
            )}  
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default AgentDetails;